import React, { useState, useEffect } from 'react';
import './GroupListByState.css';
import { useParams, useNavigate } from 'react-router-dom';
import { getToken } from '../../../../services/api';
import GroupDetailModal from './GroupDetailModal';

const GroupListByState = () => {
  const { estado } = useParams();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchGroups();
  }, [estado]);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_VERCEL_API_URL : process.env.REACT_APP_LOCAL_API_URL;
      const response = await fetch(`${API_URL}/precatorio-group-status/comercial/groups/${estado}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar grupos');
      }

      const data = await response.json();
      setGroups(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupClick = (group) => {
    setSelectedGroup(group);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedGroup(null);
    // Don't refetch all data - updates are handled by handleGroupUpdate
  };

  const handleGroupUpdate = (updatedGroupData) => {
    // Update only the specific group in the list
    setGroups(prevGroups => 
      prevGroups.map(group => 
        group._id === updatedGroupData._id 
          ? {
              ...group,
              groupStatus: updatedGroupData.groupStatus,
              commentsCount: updatedGroupData.commentsCount,
              lastComment: updatedGroupData.lastComment
            }
          : group
      )
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDocument = (type, number) => {
    if (type === 'CPF') {
      // Format CPF: 000.000.000-00
      return number.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (type === 'CNPJ') {
      // Format CNPJ: 00.000.000/0000-00
      return number.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return number;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Em Contato':
        return 'status-badge status-em-contato';
      case 'Vendendo':
        return 'status-badge status-vendendo';
      case 'Não Vende':
        return 'status-badge status-nao-vende';
      default:
        return 'status-badge status-nao-contatou';
    }
  };

  const getFilteredGroups = () => {
    return groups.filter(group => {
      // Filter by document number
      const matchesText = group.documentNumber.toLowerCase().includes(filterText.toLowerCase());
      
      // Filter by status
      const matchesStatus = filterStatus === 'all' || group.groupStatus === filterStatus;
      
      return matchesText && matchesStatus;
    });
  };

  if (loading) {
    return (
      <div className="group-list-container">
        <div className="loading">Carregando grupos...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="group-list-container">
        <div className="error">Erro: {error}</div>
      </div>
    );
  }

  const filteredGroups = getFilteredGroups();

  return (
    <div className="group-list-container">
      <div className="header-section">
        <button className="back-button" onClick={() => navigate('/comercial/meus-precatorios')}>
          ← Voltar
        </button>
        <h1>Grupos de CPF/CNPJ - {estado}</h1>
      </div>

      <div className="stats-section">
        <div className="stat-card">
          <div className="stat-label">Total de Grupos</div>
          <div className="stat-value">{groups.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total de Precatórios</div>
          <div className="stat-value">{groups.reduce((sum, g) => sum + g.totalPrecatorios, 0)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Valor Total</div>
          <div className="stat-value">{formatCurrency(groups.reduce((sum, g) => sum + g.totalValue, 0))}</div>
        </div>
      </div>

      <div className="filter-section">
        <input
          type="text"
          placeholder="Buscar por CPF/CNPJ..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="search-input"
        />
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="all">Todos os Status</option>
          <option value="Não Contatou">Não Contatou</option>
          <option value="Em Contato">Em Contato</option>
          <option value="Vendendo">Vendendo</option>
          <option value="Não Vende">Não Vende</option>
        </select>
      </div>

      <div className="groups-table-container">
        {filteredGroups.length === 0 ? (
          <div className="no-results">Nenhum grupo encontrado com os filtros aplicados.</div>
        ) : (
          <table className="groups-table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Documento</th>
                <th>Precatórios</th>
                <th>Valor Total</th>
                <th>Status</th>
                <th>Comentários</th>
                <th>Último Contato</th>
              </tr>
            </thead>
            <tbody>
              {filteredGroups.map((group) => (
                <tr 
                  key={group._id} 
                  className="group-row"
                  onClick={() => handleGroupClick(group)}
                >
                  <td>
                    <span className={`document-type-badge ${group.documentType}`}>{group.documentType}</span>
                  </td>
                  <td className="document-number">
                    {formatDocument(group.documentType, group.documentNumber)}
                  </td>
                  <td className="text-center">
                    {group.totalPrecatorios}
                  </td>
                  <td className="text-right">
                    {formatCurrency(group.totalValue)}
                  </td>
                  <td>
                    <div className={getStatusBadgeClass(group.groupStatus)}>
                      {group.groupStatus}
                    </div>
                  </td>
                  <td className="text-center">
                    {group.commentsCount}
                  </td>
                  <td className="last-contact">
                    {group.lastComment ? (
                      <div>
                        <div className="comment-preview">{group.lastComment.text}</div>
                        <div className="comment-date">
                          {new Date(group.lastComment.createdAt).toLocaleDateString('pt-BR')}
                        </div>
                      </div>
                    ) : (
                      <span className="no-contact">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && selectedGroup && (
        <GroupDetailModal
          groupId={selectedGroup._id}
          estado={estado}
          onClose={handleCloseModal}
          onGroupUpdate={handleGroupUpdate}
        />
      )}
    </div>
  );
};

export default GroupListByState;
